import { applyStudioBackgroundNow, resolveWallpaperImageUrl } from "@/studio/lib/studio-background-apply";
import { extractWallpaperPalette } from "@/studio/lib/extract-wallpaper-palette";
import { getStudioBackgroundBootInlineFragment } from "@/studio/lib/studio-background-registry";
import { AUTH_CYCLE_MS, getConvexSessionProbeInlineFragment } from "@/mos-app/studio-boot-frame-script";
import {
  DEFAULT_APPEARANCE,
  DEFAULT_WALLPAPER,
  MODE_KEY,
  SCHEME_KEY,
  STUDIO_BG_PACK_KEY,
  dropMissingAsset,
  getCachedPalette,
  getCurrentWallpaper,
  pinSavedAsset,
  readWallpaperState,
  setCachedPalette,
  setUrlHint,
  unpinSavedAsset,
  updateWallpaperCurrent,
  writeWallpaperState,
} from "@/studio/lib/wallpaper-state";

const DEFAULT_SCHEME_ID = DEFAULT_WALLPAPER.themeId;

/** Appearance mode (light/dark) + accent color schemes — CSS variables on :root */

/**
 * Accent themes, ordered round the colour wheel (picker order), Graphite last.
 * `accent` = light-mode hex (on the near-white page), `accentDark` = dark-mode
 * hex (on #111). The original eight carry `noted` (hues from Noted
 * `/opt/noted` data-color-scheme); the rest are Studio-native.
 * Accent character (Dallas, 11 Oct 2026; reference sent bubble #0c64c1 =
 * hsl(211 88% 40%)): each theme keeps its own hue, saturation ~80-90%, and a
 * lightness deep enough that WHITE text reads on it in both modes: light
 * shade >= 5:1 with white, dark shade a touch brighter but still >= 4.5:1.
 * Light hues (gold, lime, moss) go deep toward amber / olive. Muted twins
 * (rosewood, copper, sand, moss, steel, indigo, plum) sit darker than their
 * bright neighbours so themes stay distinct. Graphite stays low-saturation.
 * `accentInkDark` is the dark-mode TEXT/LINE accent (--cursor-accent-ink):
 * same hue, lighter, >= 5:1 on #111 (>= 4.5:1 on the #191919 panel), for
 * accent text, links, ticks, markers, focus rings and borders. The deep
 * `accentDark` stays the FILL (bubbles, solid buttons, send). Light mode ink =
 * the fill accent (already >= 5:1 on the page) unless `accentInk` is set.
 * Theme ids are stable (saved in localStorage and orgs).
 */
export const SCHEMES = {
  rose: {
    label: "Bloom",
    noted: "rose",
    accent: "#c90d2c",
    accentDark: "#eb0f34",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#eb4e69",
    secondary: "#f7d1d8",
    secondaryDark: "#402830",
    bg: "#161012",
    surface: "#1e1719",
    raised: "#2a2225",
  },
  cherry: {
    label: "Pulse",
    accent: "#be0e55",
    accentDark: "#e61167",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#eb4789",
    secondary: "#f2d1de",
    secondaryDark: "#452934",
    bg: "#1b1417",
    surface: "#261d20",
    raised: "#37292e",
  },
  rosewood: {
    label: "Rosewood",
    accent: "#931039",
    accentDark: "#b81447",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#eb4a7c",
    secondary: "#eadde1",
    secondaryDark: "#423538",
    bg: "#1b1718",
    surface: "#252122",
    raised: "#352f30",
  },
  coral: {
    label: "Reef",
    accent: "#be2b0e",
    accentDark: "#df3311",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#e95538",
    secondary: "#f2dcd8",
    secondaryDark: "#48302b",
    bg: "#1c1615",
    surface: "#271f1d",
    raised: "#382c2a",
  },
  ember: {
    label: "Forge",
    noted: "orange",
    accent: "#b6510a",
    accentDark: "#c2570a",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#d86818",
    secondary: "#fde4cc",
    secondaryDark: "#3d2e22",
    bg: "#161210",
    surface: "#1e1915",
    raised: "#2a231c",
  },
  copper: {
    label: "Foundry",
    accent: "#87350d",
    accentDark: "#ac4311",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#e65b1a",
    secondary: "#eadcd5",
    secondaryDark: "#3f352e",
    bg: "#1a1715",
    surface: "#24211e",
    raised: "#342f2b",
  },
  gold: {
    label: "Archive",
    noted: "amber",
    accent: "#996408",
    accentDark: "#a26a09",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#b87c14",
    secondary: "#fce9c2",
    secondaryDark: "#3d3422",
    bg: "#161310",
    surface: "#1e1a15",
    raised: "#2a241c",
  },
  sand: {
    label: "Dune",
    accent: "#774a0d",
    accentDark: "#9c6011",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#c07715",
    secondary: "#e7e0d7",
    secondaryDark: "#403a30",
    bg: "#1a1816",
    surface: "#24221f",
    raised: "#34312c",
  },
  lime: {
    label: "Canopy",
    noted: "lime",
    accent: "#4c7a08",
    accentDark: "#528408",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#609511",
    secondary: "#e6f5c8",
    secondaryDark: "#343d28",
    bg: "#121610",
    surface: "#191f17",
    raised: "#252c22",
  },
  moss: {
    label: "Moss",
    accent: "#50600b",
    accentDark: "#687e0e",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#799310",
    secondary: "#dee1d2",
    secondaryDark: "#393c2a",
    bg: "#181914",
    surface: "#22231d",
    raised: "#313229",
  },
  agent: {
    label: "Genesis",
    noted: "forest",
    accent: "#0a8035",
    accentDark: "#0a8737",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#119c43",
    secondary: "#c8ebd4",
    secondaryDark: "#283d2e",
    bg: "#101612",
    surface: "#171f1a",
    raised: "#222c26",
  },
  teal: {
    label: "Lagoon",
    accent: "#087a75",
    accentDark: "#08847e",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#119791",
    secondary: "#cfe5e4",
    secondaryDark: "#1e3f3b",
    bg: "#111a19",
    surface: "#192423",
    raised: "#233432",
  },
  ocean: {
    label: "Ocean",
    noted: "ocean",
    accent: "#0b76a8",
    accentDark: "#0b7db1",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#1690c7",
    secondary: "#c5e6f5",
    secondaryDark: "#243740",
    bg: "#101518",
    surface: "#171e22",
    raised: "#222b31",
  },
  steel: {
    label: "Harbor",
    accent: "#0d5577",
    accentDark: "#116f9c",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#1690ca",
    secondary: "#d5e1e7",
    secondaryDark: "#303b40",
    bg: "#16191a",
    surface: "#1f2325",
    raised: "#2c3234",
  },
  cobalt: {
    label: "Skyline",
    noted: "blue",
    accent: "#1050da",
    accentDark: "#316def",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#5383ec",
    secondary: "#d1dff7",
    secondaryDark: "#283240",
    bg: "#101318",
    surface: "#171b22",
    raised: "#222731",
  },
  indigo: {
    label: "Midnight",
    accent: "#113097",
    accentDark: "#1741cf",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#6181ed",
    secondary: "#d5d8e4",
    secondaryDark: "#353845",
    bg: "#17181b",
    surface: "#212226",
    raised: "#2f3037",
  },
  violet: {
    label: "Dusk",
    noted: "violet",
    accent: "#6316df",
    accentDark: "#8c4fee",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#9d6bef",
    secondary: "#e0d1f7",
    secondaryDark: "#322840",
    bg: "#131018",
    surface: "#1b1722",
    raised: "#262231",
  },
  plum: {
    label: "Plum",
    accent: "#8a0f7d",
    accentDark: "#ae139f",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#e830d6",
    secondary: "#e5d5e3",
    secondaryDark: "#3d313c",
    bg: "#191619",
    surface: "#231f23",
    raised: "#332c32",
  },
  fuchsia: {
    label: "Neon",
    accent: "#c70fac",
    accentDark: "#d510b9",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#e935cf",
    secondary: "#f0d1ec",
    secondaryDark: "#442a42",
    bg: "#1b141a",
    surface: "#261d25",
    raised: "#362935",
  },
  slate: {
    label: "Graphite",
    accent: "#515e70",
    accentDark: "#67778e",
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInkDark: "#79889e",
    secondary: "#dbdde0",
    secondaryDark: "#35373a",
    bg: "#171818",
    surface: "#212122",
    raised: "#2f3031",
  },
};


/** Retired palette ids map onto a live accent theme. */
export const ACCENT_MIGRATION = {
  mint: "agent",
  sage: "agent",
};

export function canonicalSchemeId(id) {
  const raw = id && ACCENT_MIGRATION[id] ? ACCENT_MIGRATION[id] : id;
  return SCHEMES[raw] ? raw : DEFAULT_SCHEME_ID;
}

export const STUDIO_BACKGROUND_FAMILIES = {
  animated: {
    label: "Animated",
    description: "Illustrated cartoon matte environments (dark + light)",
  },
  cinematic: {
    label: "Cinematic",
    description: "Photoreal film-set moods (dark + light)",
  },
};

/** @deprecated use STUDIO_BACKGROUND_FAMILIES */
export const STUDIO_BACKGROUND_PACKS = {
  animated: { label: "Animated" },
  worlds: { label: "Scenes" },
  clean: { label: "Clean" },
};

const BG_FAMILY_MIGRATION = {
  worlds: "animated",
  space: "animated",
  animated: "animated",
  cinematic: "cinematic",
  spacey: "animated",
  scenic: "animated",
  clean: "animated",
};

function normalizeBgFamily(id) {
  if (!id) return "animated";
  return BG_FAMILY_MIGRATION[id] ?? (STUDIO_BACKGROUND_FAMILIES[id] ? id : "animated");
}

/* Shared neutrals. A theme only changes the accent; light and dark
 * surfaces stay on this base so every theme looks the same in both modes. */
const LIGHT_BASE = {
  bg: "#f9f9f9",       // --background
  surface: "#fcfcfc",  // --card / page
  raised: "#efefef",   // --muted (L3)
  text: "#202020",     // --foreground
  textMuted: "#646464", // --muted-foreground
  textFaint: "#8a8a8a",
  border: "rgba(0,0,0,0.12)",
  borderSoft: "rgba(0,0,0,0.08)",
  hover: "#e8e8e8",    // --accent
  active: "#d8d8d8",   // --border
};

const DARK_BASE = {
  page: "#111111",
  sidebar: "#0e0e0e",
  panel: "#191919",
  plate: "#161616",
  plateStrong: "#222222",
  composer: "#141414",
  surface: "#1c1c1c",
  hover: "#242424",
  active: "#2e2e2e",
  overlay: "#2a2a2a",
  text: "#eeeeee",
  textSoft: "rgba(238,238,238,0.92)",
  textMuted: "rgba(238,238,238,0.62)",
  textFaint: "rgba(238,238,238,0.45)",
};

function parseHex(hex) {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function mixHex(a, b, t) {
  const c1 = parseHex(a);
  const c2 = parseHex(b);
  const f = (x, y) => Math.round(x + (y - x) * t);
  return `#${[f(c1.r, c2.r), f(c1.g, c2.g), f(c1.b, c2.b)]
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("")}`;
}

function lighten(hex, amt = 0.12) {
  return mixHex(hex, "#ffffff", amt);
}

function darken(hex, amt = 0.12) {
  return mixHex(hex, "#000000", amt);
}

function accentDim(hex) {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r},${g},${b},0.14)`;
}

function accentBorder(hex) {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r},${g},${b},0.28)`;
}

function accentHover(hex) {
  return lighten(hex, 0.1);
}

function hexLuminance(hex) {
  const { r, g, b } = parseHex(hex);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function resolveSchemeAccent(scheme, isLight) {
  if (!scheme) return { accent: "#405a64", accentInk: "#405a64", accentFg: "#ffffff", secondary: "#b5d5ff" };
  if (isLight) {
    return {
      accent: scheme.accent,
      accentInk: scheme.accentInk || scheme.accent,
      accentFg: scheme.accentFg || (hexLuminance(scheme.accent) > 0.62 ? "#171717" : "#ffffff"),
      secondary: scheme.secondary || lighten(scheme.accent, 0.72),
    };
  }
  const accent = scheme.accentDark || scheme.accent;
  return {
    accent,
    accentInk: scheme.accentInkDark || accent,
    accentFg: scheme.accentDarkFg || (hexLuminance(accent) > 0.62 ? "#111111" : "#ffffff"),
    secondary: scheme.secondaryDark || darken(accent, 0.55),
  };
}


/** Hairline borders — low-contrast rgba, not lightened hex stripes. */
function hairlineBorder(isLight, alpha) {
  return isLight ? `rgba(0, 0, 0, ${alpha})` : `rgba(255, 255, 255, ${alpha})`;
}

function resolveSchemeForWallpaper(wallpaper, _cached) {
  const fallbackId =
    wallpaper?.kind === "preset" && SCHEMES[wallpaper.themeId]
      ? wallpaper.themeId
      : DEFAULT_SCHEME_ID;
  const base = SCHEMES[fallbackId] ?? SCHEMES[DEFAULT_SCHEME_ID] ?? SCHEMES.agent;
  /* Wallpaper photos must not recolor chrome. Accent comes from the named theme. */
  return { scheme: base, schemeId: fallbackId };
}

function buildDeskPalette(scheme, isLight) {
  const tones = resolveSchemeAccent(scheme, isLight);
  if (isLight) {
    /* Noted light neutrals: page lightest → plate → plateStrong. */
    const page = LIGHT_BASE.surface;
    const plate = LIGHT_BASE.bg;
    const plateStrong = LIGHT_BASE.raised;
    return {
      /* Legacy: light mos-bg = plate, mos-panel = page (admin swap). */
      bg: plate,
      sidebar: plate,
      panel: page,
      composer: mixHex(plate, page, 0.38),
      surface: mixHex(plate, page, 0.55),
      surfaceRaised: plateStrong,
      surfaceOverlay: LIGHT_BASE.hover,
      surfaceInput: page,
      page,
      plate,
      plateStrong,
      border: hairlineBorder(true, 0.12),
      borderSoft: hairlineBorder(true, 0.08),
      borderSubtle: hairlineBorder(true, 0.09),
      borderFocus: mixHex(tones.accent, "#000000", 0.28),
      text: LIGHT_BASE.text,
      textSoft: mixHex(LIGHT_BASE.text, LIGHT_BASE.textMuted, 0.35),
      muted: LIGHT_BASE.textMuted,
      faint: LIGHT_BASE.textFaint,
      hover: LIGHT_BASE.hover,
      active: LIGHT_BASE.active,
      accent: tones.accent,
      accentInk: tones.accentInk,
      accentFg: tones.accentFg,
      secondary: tones.secondary,
    };
  }

  /* Dark: one neutral base. Scheme hue stays on the accent only. */
  const page = DARK_BASE.page;
  const panel = DARK_BASE.panel;
  const plateStrong = DARK_BASE.plateStrong;
  const plate = DARK_BASE.plate;

  return {
    bg: page,
    sidebar: DARK_BASE.sidebar,
    panel,
    composer: DARK_BASE.composer,
    surface: DARK_BASE.surface,
    surfaceRaised: plateStrong,
    surfaceOverlay: DARK_BASE.overlay,
    surfaceInput: panel,
    page,
    plate,
    plateStrong,
    border: hairlineBorder(false, 0.10),
    borderSoft: hairlineBorder(false, 0.06),
    borderSubtle: hairlineBorder(false, 0.08),
    borderFocus: mixHex(tones.accentInk || tones.accent, page, 0.15),
    text: DARK_BASE.text,
    textSoft: DARK_BASE.textSoft,
    muted: DARK_BASE.textMuted,
    faint: DARK_BASE.textFaint,
    hover: DARK_BASE.hover,
    active: DARK_BASE.active,
    accent: tones.accent,
    accentInk: tones.accentInk,
    accentFg: tones.accentFg,
    secondary: tones.secondary,
  };
}

function overlayTokens(isLight) {
  if (isLight) {
    return {
      subtle: "rgba(0, 0, 0, 0.04)",
      hover: "rgba(0, 0, 0, 0.06)",
      muted: "rgba(0, 0, 0, 0.025)",
      track: "transparent",
      thumb: "rgba(0, 0, 0, 0.18)",
      thumbHover: "rgba(0, 0, 0, 0.28)",
    };
  }
  return {
    subtle: "rgba(255, 255, 255, 0.05)",
    hover: "rgba(255, 255, 255, 0.08)",
    muted: "rgba(255, 255, 255, 0.03)",
    track: "transparent",
    thumb: "rgba(255, 255, 255, 0.2)",
    thumbHover: "rgba(255, 255, 255, 0.34)",
  };
}

function applyDeskTokens(palette, isLight) {
  const root = document.documentElement;
  const accent = palette.accent;
  /* Text / line accent: brighter in dark mode so it reads on the page. */
  const accentInk = palette.accentInk || accent;
  const accentFg = palette.accentFg || (hexLuminance(accent) > 0.62 ? "#171717" : "#ffffff");
  const secondary = palette.secondary || (isLight ? lighten(accent, 0.72) : darken(accent, 0.55));
  const hover = accentHover(accent);
  const { r, g, b } = parseHex(accent);
  const overlays = overlayTokens(isLight);

  root.style.setProperty("--cursor-accent", accent);
  root.style.setProperty("--cursor-accent-hover", hover);
  root.style.setProperty("--cursor-accent-dim", accentDim(accent));
  root.style.setProperty("--cursor-accent-border", accentBorder(accentInk));
  root.style.setProperty("--cursor-accent-ink", accentInk);
  root.style.setProperty("--mos-accent-ink", accentInk);
  root.style.setProperty("--cursor-accent-fg", accentFg);
  root.style.setProperty("--cursor-sidebar", palette.sidebar);
  root.style.setProperty("--mos-accent-fg", accentFg);
  root.style.setProperty("--mos-secondary", secondary);
  root.style.setProperty("--primary-foreground", accentFg);
  root.style.setProperty("--secondary", secondary);

  root.style.setProperty("--cursor-overlay-subtle", overlays.subtle);
  root.style.setProperty("--cursor-overlay-hover", overlays.hover);
  root.style.setProperty("--cursor-overlay-muted", overlays.muted);

  root.style.setProperty("--mos-bg", palette.bg);
  root.style.setProperty("--mos-sidebar", palette.sidebar);
  root.style.setProperty("--mos-panel", palette.panel);
  root.style.setProperty("--mos-raised", palette.surfaceRaised);
  /* Appearance-agnostic chrome roles — dark keeps scheme hue. */
  root.style.setProperty("--mos-page", palette.page ?? palette.panel);
  root.style.setProperty("--mos-plate", palette.plate ?? palette.bg);
  root.style.setProperty(
    "--mos-plate-strong",
    palette.plateStrong ?? palette.surfaceRaised
  );
  root.style.setProperty("--mos-composer", palette.composer);
  root.style.setProperty("--mos-surface", palette.surface);
  root.style.setProperty("--mos-border", palette.border);
  root.style.setProperty("--mos-border-soft", palette.borderSoft);
  root.style.setProperty("--mos-border-subtle", palette.borderSubtle);
  root.style.setProperty("--mos-text", palette.text);
  root.style.setProperty("--mos-text-soft", palette.textSoft);
  root.style.setProperty("--mos-text-bright", palette.text);
  root.style.setProperty("--mos-muted", palette.muted);
  root.style.setProperty("--mos-faint", palette.faint);
  root.style.setProperty("--mos-accent", accent);
  root.style.setProperty("--mos-accent-hover", hover);
  root.style.setProperty("--mos-hover", palette.hover);
  root.style.setProperty("--mos-active", palette.active);

  root.style.setProperty("--color-cursor-bg", palette.bg);
  root.style.setProperty("--color-cursor-sidebar", palette.sidebar);
  root.style.setProperty("--color-cursor-editor", palette.panel);
  root.style.setProperty("--color-cursor-chat", palette.bg);
  root.style.setProperty("--color-cursor-composer", palette.composer);
  root.style.setProperty("--color-cursor-border", palette.border);
  root.style.setProperty("--color-cursor-border-soft", palette.borderSoft);
  root.style.setProperty("--color-cursor-border-subtle", palette.borderSubtle);
  root.style.setProperty("--color-cursor-muted", palette.muted);
  root.style.setProperty("--color-cursor-text", palette.text);
  root.style.setProperty("--color-cursor-text-bright", palette.text);
  root.style.setProperty("--color-cursor-text-soft", palette.textSoft);
  root.style.setProperty("--color-cursor-hover", palette.hover);
  root.style.setProperty("--color-cursor-active", palette.active);
  root.style.setProperty("--color-cursor-accent", accent);
  root.style.setProperty("--color-cursor-fg", palette.text);

  root.style.setProperty("--cursor-surface", palette.surface);
  root.style.setProperty("--cursor-surface-raised", palette.surfaceRaised);
  root.style.setProperty("--cursor-surface-overlay", palette.surfaceOverlay);
  root.style.setProperty("--cursor-surface-input", palette.surfaceInput);
  root.style.setProperty("--cursor-border-subtle", palette.borderSubtle);
  root.style.setProperty("--cursor-border-focus", palette.borderFocus);

  root.style.setProperty("--accent", accent);
  root.style.setProperty("--accent-dim", accentDim(accent));
  root.style.setProperty("--accent-rgb", `${r},${g},${b}`);
  root.style.setProperty("--bg", palette.bg);
  root.style.setProperty("--surface", palette.panel);
  root.style.setProperty("--surface-raised", palette.surfaceRaised);
  root.style.setProperty("--surface-hover", palette.hover);
  root.style.setProperty("--border", palette.border);
  root.style.setProperty("--border-strong", palette.borderSubtle);
  root.style.setProperty("--ring", `rgba(${r},${g},${b},0.32)`);

  root.style.setProperty("--scrollbar-track", overlays.track);
  root.style.setProperty("--scrollbar-thumb", overlays.thumb);
  root.style.setProperty("--scrollbar-thumb-hover", overlays.thumbHover);

  root.style.setProperty("--text", palette.text);
  root.style.setProperty("--text-muted", palette.muted);
  root.style.setProperty("--text-faint", palette.faint);
  // The boot script sets this too, so a mode switch must refresh it.
  root.style.setProperty("--chat-text-color", palette.text);
}

function migrateLegacy() {
  const id = localStorage.getItem(SCHEME_KEY);
  if (id === "light" && !localStorage.getItem(MODE_KEY)) {
    localStorage.setItem(MODE_KEY, "light");
    localStorage.setItem(SCHEME_KEY, DEFAULT_SCHEME_ID);
  }
  // Ensure wallpaper state exists (migrates from scheme + family keys).
  readWallpaperState();
}

export function getAppearanceMode() {
  migrateLegacy();
  const mode = localStorage.getItem(MODE_KEY);
  if (mode === "dark") return "dark";
  if (mode === "light") return "light";
  return DEFAULT_APPEARANCE;
}

export function getSchemeId() {
  migrateLegacy();
  const wallpaper = getCurrentWallpaper();
  if (wallpaper.kind === "preset") return canonicalSchemeId(wallpaper.themeId);
  const id = localStorage.getItem(SCHEME_KEY);
  if (id === "light") return DEFAULT_SCHEME_ID;
  return canonicalSchemeId(id);
}

export function getWallpaper() {
  migrateLegacy();
  return getCurrentWallpaper();
}

export function getWallpaperState() {
  migrateLegacy();
  return readWallpaperState();
}

export function getStudioBackgroundFamily() {
  const wallpaper = getCurrentWallpaper();
  if (wallpaper.kind === "preset") return normalizeBgFamily(wallpaper.family);
  const id = localStorage.getItem(STUDIO_BG_PACK_KEY);
  const family = normalizeBgFamily(id);
  if (id && family !== id) {
    localStorage.setItem(STUDIO_BG_PACK_KEY, family);
  }
  return family;
}

/** @deprecated use getStudioBackgroundFamily */
export function getStudioBackgroundPack() {
  return getStudioBackgroundFamily();
}

/**
 * Next preset for the logo / background shuffle. The accent theme always
 * changes (never the current scheme), so every press shows a new colour;
 * the background family stays random.
 */
function pickNextPresetWallpaper(currentSchemeId) {
  const families = Object.keys(STUDIO_BACKGROUND_FAMILIES);
  const current = canonicalSchemeId(currentSchemeId);
  const others = Object.keys(SCHEMES).filter((id) => id !== current);
  const pool = others.length ? others : Object.keys(SCHEMES);
  const family = families[Math.floor(Math.random() * families.length)] ?? "animated";
  const themeId = pool[Math.floor(Math.random() * pool.length)] ?? "agent";
  return { kind: "preset", family: normalizeBgFamily(family), themeId };
}

function writeStudioBackgroundFamily(id) {
  const family = normalizeBgFamily(id);
  document.documentElement.dataset.studioBgFamily = family;
  document.documentElement.dataset.studioBgPack = family === "animated" ? "worlds" : family;
  localStorage.setItem(STUDIO_BG_PACK_KEY, family);
  return family;
}

export function setStudioBackgroundFamily(id) {
  const family = writeStudioBackgroundFamily(id);
  const current = getCurrentWallpaper();
  const themeId =
    current.kind === "preset" && SCHEMES[current.themeId] ? current.themeId : getSchemeId();
  void setWallpaper({ kind: "preset", family, themeId });
}

/** @deprecated use setStudioBackgroundFamily */
export function setStudioBackgroundPack(id) {
  setStudioBackgroundFamily(id);
}

/** @deprecated use getSchemeId */
export function getThemeId() {
  return getSchemeId();
}

function syncAppearanceControls(mode) {
  document.querySelectorAll("#appearance-mode-seg .seg-btn, #theme-appearance-seg .seg-btn").forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.mode === mode);
  });
}

function syncSchemeChips(schemeId) {
  document.querySelectorAll(".theme-chip").forEach((chip) => {
    chip.classList.toggle("active", chip.dataset.theme === schemeId);
  });
}

function syncWallpaperDataset(wallpaper) {
  const root = document.documentElement;
  if (wallpaper.kind === "preset") {
    root.dataset.theme = wallpaper.themeId;
    root.dataset.studioBgFamily = wallpaper.family;
    root.dataset.studioBgPack = wallpaper.family === "animated" ? "worlds" : wallpaper.family;
    root.dataset.wallpaperKind = "preset";
    delete root.dataset.wallpaperAssetId;
  } else {
    root.dataset.theme = getSchemeId();
    root.dataset.wallpaperKind = "asset";
    root.dataset.wallpaperAssetId = wallpaper.assetId;
  }
}

async function extractAndCacheForWallpaper(wallpaper, imageUrl) {
  if (!imageUrl) return null;
  const extracted = await extractWallpaperPalette(imageUrl, {
    crossOrigin: wallpaper.kind === "asset" || Boolean(process.env.NEXT_PUBLIC_STUDIO_BG_CDN),
  });
  if (!extracted?.accent) return null;
  setCachedPalette(wallpaper, extracted);
  return extracted;
}

let themeGradientClockInstalled = false;

/**
 * The theme gradient (studio-boot-frame.css, ysbf-theme-cycle) runs on the
 * sign-in page, the boot frame and the signed-in .studio-stage::before. Each
 * starts its CSS animation when its element appears, so put every one on the
 * same wall-clock phase: the colour then carries straight on from sign-in into
 * the app instead of jumping. One listener, runs only when such an animation
 * starts.
 */
function installThemeGradientClock() {
  if (themeGradientClockInstalled || typeof document === "undefined") return;
  if (typeof document.getAnimations !== "function") return;
  themeGradientClockInstalled = true;
  const align = (anim) => {
    try {
      const delay = Number(anim.effect?.getTiming?.().delay) || 0;
      anim.currentTime = delay + (Date.now() % AUTH_CYCLE_MS);
    } catch {
      /* animation gone */
    }
  };
  const alignFor = (target) => {
    for (const anim of document.getAnimations()) {
      if (anim.animationName !== "ysbf-theme-cycle") continue;
      if (target && anim.effect?.target !== target) continue;
      align(anim);
    }
  };
  document.addEventListener("animationstart", (event) => {
    if (event.animationName === "ysbf-theme-cycle") alignFor(event.target);
  });
  alignFor(null);
}

// Installed when theme.js loads (the sign-in page and the app both import it;
// the inline boot script, not applyTheme, sets the theme on a normal load).
if (typeof window !== "undefined") installThemeGradientClock();

/** Free accent hue (logo drag, like Sanity). Overrides the named scheme's accent tones; cleared by picking/shuffling a scheme. */
export const ACCENT_HUE_KEY = "mercuryos-accent-hue-v1";
export function customAccentFromHue(hue) {
  var h = ((Number(hue) % 360) + 360) % 360;
  function hx(s, l) {
    var a = s * Math.min(l, 1 - l);
    function f(n) {
      var k = (n + h / 30) % 12;
      var c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
      return Math.round(c * 255).toString(16).padStart(2, "0");
    }
    return "#" + f(0) + f(8) + f(4);
  }
  return {
    accent: hx(0.85, 0.4),
    accentDark: hx(0.85, 0.5),
    accentFg: "#ffffff",
    accentDarkFg: "#ffffff",
    accentInk: hx(0.85, 0.36),
    accentInkDark: hx(0.9, 0.68),
    secondary: hx(0.6, 0.9),
    secondaryDark: hx(0.25, 0.2),
  };
}
export function getCustomAccentHue() {
  try {
    var v = localStorage.getItem(ACCENT_HUE_KEY);
    if (v === null || v === "" || isNaN(Number(v))) return null;
    return Number(v);
  } catch {
    return null;
  }
}
function clearCustomAccentHue() {
  try { localStorage.removeItem(ACCENT_HUE_KEY); } catch { /* ignore */ }
}
/** Live/persisted free accent: hue 0-360 + light/dark. */
export function setCustomAccent(hue, mode) {
  try { localStorage.setItem(ACCENT_HUE_KEY, String(Math.round(((hue % 360) + 360) % 360))); } catch { /* ignore */ }
  applyTheme(getSchemeId(), mode === "light" ? "light" : "dark");
}

export function applyTheme(schemeId, mode) {
  migrateLegacy();
  installThemeGradientClock();
  const wallpaper = getCurrentWallpaper();
  const appearance = mode === "light" || mode === "dark" ? mode : getAppearanceMode();
  const cached = getCachedPalette(wallpaper);
  const sid =
    schemeId && SCHEMES[schemeId]
      ? schemeId
      : wallpaper.kind === "preset" && SCHEMES[wallpaper.themeId]
        ? wallpaper.themeId
        : getSchemeId();
  const { scheme } = resolveSchemeForWallpaper(
    wallpaper.kind === "preset" ? { ...wallpaper, themeId: sid } : wallpaper,
    cached,
  );
  const isLight = appearance === "light";
  const customHue = getCustomAccentHue();
  const palette = buildDeskPalette(
    customHue == null ? scheme : { ...scheme, ...customAccentFromHue(customHue) },
    isLight,
  );

  const root = document.documentElement;
  root.dataset.appearance = appearance;
  // Native form controls, scrollbars and the canvas follow the chosen mode, not
  // the OS. The inline boot script sets the same value before first paint.
  root.style.colorScheme = appearance;
  syncNativeAppearance(appearance);
  syncWallpaperDataset(
    wallpaper.kind === "preset" ? { kind: "preset", family: wallpaper.family, themeId: sid } : wallpaper,
  );

  applyDeskTokens(palette, isLight);

  root.style.setProperty("--danger", "#dc2626");
  root.style.setProperty("--danger-fg", "#ffffff");
  root.style.setProperty("--danger-rgb", "220,38,38");
  root.style.setProperty("--danger-dim", "rgba(220,38,38,0.14)");

  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", palette.bg);
  if (wallpaper.kind === "preset") {
    localStorage.setItem(SCHEME_KEY, sid);
    localStorage.setItem(STUDIO_BG_PACK_KEY, wallpaper.family);
  } else {
    localStorage.setItem(SCHEME_KEY, sid);
  }
  localStorage.setItem(MODE_KEY, appearance);
  syncSchemeChips(sid);
  syncAppearanceControls(appearance);

  const bgFamilyNow = getStudioBackgroundFamily();
  window.dispatchEvent(
    new CustomEvent("mercuryos-theme-change", {
      detail: {
        schemeId: sid,
        mode: appearance,
        bgFamily: bgFamilyNow,
        bgPack: bgFamilyNow === "animated" ? "worlds" : bgFamilyNow,
        wallpaper,
      },
    }),
  );
  syncStudioBackgroundCss();
}

let lastNativeAppearance = null;

/**
 * Capacitor Android shell: MainActivity exposes YatisharaShell.setAppearance,
 * which persists the mode and repaints the window, status bar and WebView
 * background. The next cold launch then opens on the right colour instead of
 * the fixed light launch background.
 */
function syncNativeAppearance(mode) {
  if (typeof window === "undefined" || mode === lastNativeAppearance) return;
  lastNativeAppearance = mode;
  try {
    window.YatisharaShell?.setAppearance?.(mode);
  } catch {
    /* not in the native shell */
  }
}

function syncStudioBackgroundCss() {
  if (typeof window === "undefined") return;
  applyStudioBackgroundNow();
}

export function setAppearanceMode(mode) {
  // Keep the same wallpaper; only remap light/dark tokens.
  applyTheme(getSchemeId(), mode === "light" ? "light" : "dark");
}

export function setColorScheme(id) {
  clearCustomAccentHue();
  const family = getStudioBackgroundFamily();
  void setWallpaper({
    kind: "preset",
    family,
    themeId: SCHEMES[id] ? id : DEFAULT_SCHEME_ID,
  });
}

/**
 * Set current wallpaper (preset or asset), derive/cache palette, apply tokens + backdrop.
 * @param {{ kind: "preset", family: string, themeId: string } | { kind: "asset", assetId: string }} ref
 * @param {{ url?: string, extract?: boolean }} [opts]
 */
export async function setWallpaper(ref, opts = {}) {
  migrateLegacy();
  const wallpaper =
    ref?.kind === "asset" && ref.assetId
      ? { kind: "asset", assetId: ref.assetId }
      : {
          kind: "preset",
          family: normalizeBgFamily(ref?.family),
          themeId: SCHEMES[ref?.themeId] ? ref.themeId : DEFAULT_SCHEME_ID,
        };

  if (wallpaper.kind === "asset" && opts.url) {
    setUrlHint(wallpaper.assetId, opts.url);
  }

  updateWallpaperCurrent(wallpaper);
  if (wallpaper.kind === "preset") {
    writeStudioBackgroundFamily(wallpaper.family);
    localStorage.setItem(SCHEME_KEY, wallpaper.themeId);
  }

  const appearance = getAppearanceMode();
  applyTheme(
    wallpaper.kind === "preset" ? wallpaper.themeId : getSchemeId(),
    appearance,
  );

  if (opts.extract === false) return wallpaper;

  const imageUrl = opts.url ?? resolveWallpaperImageUrl(wallpaper, appearance);
  if (imageUrl) {
    const extracted = await extractAndCacheForWallpaper(wallpaper, imageUrl);
    if (extracted) {
      applyTheme(
        wallpaper.kind === "preset" ? wallpaper.themeId : getSchemeId(),
        appearance,
      );
    }
  }

  return wallpaper;
}

/** Pin an asset as a saved wallpaper without necessarily applying it. */
export function saveWallpaperAsset(assetId, url) {
  if (!assetId) return;
  pinSavedAsset(assetId);
  if (url) setUrlHint(assetId, url);
  window.dispatchEvent(
    new CustomEvent("mercuryos-theme-change", {
      detail: { wallpaper: getCurrentWallpaper(), saved: true },
    }),
  );
}

export function removeSavedWallpaper(assetId) {
  const state = unpinSavedAsset(assetId);
  applyTheme(
    state.current.kind === "preset" ? state.current.themeId : getSchemeId(),
    getAppearanceMode(),
  );
}

/** Refresh signed URL for the active asset wallpaper and re-paint. */
export function refreshAssetWallpaperUrl(assetId, url) {
  if (!assetId || !url) return;
  setUrlHint(assetId, url);
  const current = getCurrentWallpaper();
  if (current.kind === "asset" && current.assetId === assetId) {
    applyStudioBackgroundNow();
    void extractAndCacheForWallpaper(current, url).then((extracted) => {
      if (extracted) applyTheme(getSchemeId(), getAppearanceMode());
    });
  }
}

/** Fallback when a custom wallpaper asset is deleted or fails to load. */
export function fallbackWallpaper(missingAssetId) {
  const state = missingAssetId
    ? dropMissingAsset(missingAssetId)
    : (() => {
        const s = readWallpaperState();
        s.current = { ...DEFAULT_WALLPAPER };
        writeWallpaperState(s);
        return s;
      })();

  writeStudioBackgroundFamily(state.current.family);
  localStorage.setItem(SCHEME_KEY, state.current.themeId);
  applyTheme(state.current.themeId, getAppearanceMode());
  return state.current;
}

/** Use a Files image asset as wallpaper (apply + pin). */
export async function useAssetAsWallpaper(assetId, url) {
  if (!assetId) return null;
  return setWallpaper({ kind: "asset", assetId }, { url });
}

/**
 * Logo / background press: always flips light <-> dark and always picks a new
 * accent theme. Each scheme carries its own light (`accent`) and dark
 * (`accentDark`) hue, so applyTheme resolves the variant tuned for the mode
 * being switched to.
 */
export function randomizeStudioAppearance() {
  clearCustomAccentHue();
  const next = pickNextPresetWallpaper(getSchemeId());
  const nextMode = getAppearanceMode() === "dark" ? "light" : "dark";
  localStorage.setItem(MODE_KEY, nextMode);
  writeStudioBackgroundFamily(next.family);
  void setWallpaper(next);
  return { family: next.family, schemeId: next.themeId, mode: nextMode, wallpaper: next };
}

/** @deprecated use randomizeStudioAppearance */
export function randomizeTheme() {
  randomizeStudioAppearance();
}

export function initTheme() {
  migrateLegacy();
  applyTheme(getSchemeId(), getAppearanceMode());
}

const wiredPickers = new WeakSet();
const wiredModeSegs = new WeakSet();

export function wireThemePicker(container) {
  if (!container || wiredPickers.has(container)) return;
  wiredPickers.add(container);
  container.innerHTML = "";
  const current = getSchemeId();
  for (const [id, t] of Object.entries(SCHEMES)) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `theme-chip${id === current ? " active" : ""}`;
    btn.dataset.theme = id;
    btn.setAttribute("aria-label", t.label);
    btn.innerHTML = `<span class="theme-chip-swatch" style="background:${t.accent}"></span><span class="theme-chip-label">${t.label}</span>`;
    btn.addEventListener("click", () => setColorScheme(id));
    container.appendChild(btn);
  }
}

export function wireAppearanceMode(container) {
  if (!container || wiredModeSegs.has(container)) return;
  wiredModeSegs.add(container);
  const current = getAppearanceMode();
  container.querySelectorAll(".seg-btn").forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.mode === current);
    btn.addEventListener("click", () => {
      const mode = btn.dataset.mode;
      if (mode === "light" || mode === "dark") setAppearanceMode(mode);
    });
  });
}

export function wireAppearanceSettings() {
  wireAppearanceMode(document.querySelector("#appearance-mode-seg"));
  wireAppearanceMode(document.querySelector("#theme-appearance-seg"));
  wireThemePicker(document.querySelector("#theme-picker"));
  wireThemePicker(document.querySelector("#theme-sheet-picker"));
}

/** Blocking inline script for layout — sets --mos-* before first paint (Tailwind reads these). */
export function getThemeBootInlineScript() {
  const schemesJson = JSON.stringify(SCHEMES);
  const bgMigrationJson = JSON.stringify(BG_FAMILY_MIGRATION);
  const accentMigrationJson = JSON.stringify(ACCENT_MIGRATION);
  const bgFamilyKeysJson = JSON.stringify(Object.keys(STUDIO_BACKGROUND_FAMILIES));
  const sessionProbe = getConvexSessionProbeInlineFragment();
  const wallpaperBoot = getStudioBackgroundBootInlineFragment();
  return `(function(){try{${sessionProbe}var SCHEMES=${schemesJson};var BG_MIG=${bgMigrationJson};var SK="mercuryos-theme-v1",MK="mercuryos-appearance-v1",BK="mercuryos-studio-bg-pack-v1",WK="mercuryos-wallpaper-v1";var DARK_INK="#111111";function parseHex(h){h=h.replace("#","");return{r:parseInt(h.slice(0,2),16),g:parseInt(h.slice(2,4),16),b:parseInt(h.slice(4,6),16)}}function mixHex(a,b,t){function f(x,y){return Math.round(x+(y-x)*t)}var c1=parseHex(a),c2=parseHex(b);return"#"+[f(c1.r,c2.r),f(c1.g,c2.g),f(c1.b,c2.b)].map(function(v){return v.toString(16).padStart(2,"0")}).join("")}function darken(h,a){return mixHex(h,"#000000",a||0.12)}function lighten(h,a){return mixHex(h,"#ffffff",a||0.12)}function hexLum(h){var p=parseHex(h);return(0.2126*p.r+0.7152*p.g+0.0722*p.b)/255}function deepenDark(h,toward,maxLum){var mixed=mixHex(h,DARK_INK,toward==null?0.45:toward);var lum=hexLum(mixed);if(lum<=(maxLum==null?0.1:maxLum))return mixed;var scale=(maxLum==null?0.1:maxLum)/lum;var p=parseHex(mixed);return"#"+[p.r,p.g,p.b].map(function(v){return Math.max(0,Math.min(255,Math.round(v*scale))).toString(16).padStart(2,"0")}).join("")}function hairlineBorder(isLight,a){return isLight?"rgba(0,0,0,"+a+")":"rgba(255,255,255,"+a+")"}function accentDim(hex){var p=parseHex(hex);return"rgba("+p.r+","+p.g+","+p.b+",0.14)"}function accentBorder(hex){var p=parseHex(hex);return"rgba("+p.r+","+p.g+","+p.b+",0.28)"}function accentHover(hex){return lighten(hex,0.1)}function normBg(id){if(!id)return"animated";return BG_MIG[id]||id||"animated"}var ACCENT_MIG=${accentMigrationJson};var LANDING_BG_KEYS=${bgFamilyKeysJson};var lp=location.pathname||"/";var landingApp=lp==="/"||/^\\/u\\/[^\\/]+\\/?$/.test(lp);if(landingApp&&!__ysHasConvexSession()){var fam=LANDING_BG_KEYS[Math.floor(Math.random()*LANDING_BG_KEYS.length)]||"animated";fam=normBg(fam);var skeys=Object.keys(SCHEMES);var rsid=skeys[Math.floor(Math.random()*skeys.length)]||"gold";var rmode=Math.random()<0.5?"light":"dark";if(rsid==="light"){rmode="light";rsid="gold"}if(ACCENT_MIG[rsid])rsid=ACCENT_MIG[rsid];if(!SCHEMES[rsid])rsid="gold";localStorage.setItem(SK,rsid);localStorage.setItem(MK,rmode);localStorage.setItem(BK,fam);try{var ws=JSON.parse(localStorage.getItem(WK)||"null");if(!ws||typeof ws!=="object")ws={current:null,palettes:{},urlHints:{},pinned:[]};ws.current={kind:"preset",family:fam,themeId:rsid};localStorage.setItem(WK,JSON.stringify(ws))}catch(e){}}var wpState=null;try{wpState=JSON.parse(localStorage.getItem(WK)||"null")}catch(e){wpState=null}var sid=localStorage.getItem(SK)||"gold";var mode=localStorage.getItem(MK)||"light";var storedBg=localStorage.getItem(BK);var bgFamily=normBg(storedBg);var assetUrlHint=null;var cachedPal=null;if(wpState&&wpState.current){if(wpState.current.kind==="preset"){sid=wpState.current.themeId||sid;bgFamily=normBg(wpState.current.family)}else if(wpState.current.kind==="asset"){var ak="asset:"+wpState.current.assetId;assetUrlHint=wpState.urlHints&&wpState.urlHints[ak];cachedPal=wpState.palettes&&wpState.palettes[ak]}if(wpState.current.kind==="preset"){var pk="preset:"+bgFamily+":"+sid;cachedPal=wpState.palettes&&wpState.palettes[pk]}}if(/^\\/oauth\\/mcp\\/?$/.test(lp)){/* consent page: landing-style random accent and mode per visit, not persisted */var cks=Object.keys(SCHEMES);sid=cks[Math.floor(Math.random()*cks.length)]||"gold";mode=Math.random()<0.5?"light":"dark";wpState=null}if(sid==="light"){mode="light";sid="gold"}if(ACCENT_MIG[sid])sid=ACCENT_MIG[sid];if(!SCHEMES[sid])sid="gold";var scheme=Object.assign({},SCHEMES[sid]);try{var noHue=(landingApp&&!__ysHasConvexSession())||lp.indexOf("/oauth/mcp")===0;var chv=localStorage.getItem("mercuryos-accent-hue-v1");if(!noHue&&chv!==null&&chv!==""&&!isNaN(+chv)){Object.assign(scheme,(${customAccentFromHue.toString()})(+chv))}}catch(e){}/* named theme owns the accent; wallpaper cache must not recolor chrome */var isLight=mode==="light";var resolvedAccent=isLight?scheme.accent:(scheme.accentDark||scheme.accent);var resolvedFg=isLight?(scheme.accentFg||"#ffffff"):(scheme.accentDarkFg||"#111111");var resolvedSecondary=isLight?(scheme.secondary||scheme.accent):(scheme.secondaryDark||scheme.accent);var resolvedInk=isLight?(scheme.accentInk||scheme.accent):(scheme.accentInkDark||scheme.accentDark||scheme.accent);scheme.accent=resolvedAccent;var LIGHT={bg:"#f9f9f9",surface:"#fcfcfc",raised:"#efefef",text:"#202020",textMuted:"#646464",textFaint:"#8a8a8a",hover:"#e8e8e8",active:"#d8d8d8"};var palette;if(isLight){var lPage=LIGHT.surface,lPlate=LIGHT.bg,lStrong=LIGHT.raised;palette={bg:lPlate,sidebar:lPlate,panel:lPage,composer:mixHex(lPlate,lPage,0.38),surface:mixHex(lPlate,lPage,0.55),surfaceRaised:lStrong,surfaceOverlay:LIGHT.hover,surfaceInput:lPage,page:lPage,plate:lPlate,plateStrong:lStrong,border:hairlineBorder(true,0.12),borderSoft:hairlineBorder(true,0.08),borderSubtle:hairlineBorder(true,0.09),borderFocus:mixHex(scheme.accent,"#000000",0.28),text:LIGHT.text,textSoft:mixHex(LIGHT.text,LIGHT.textMuted,0.35),muted:LIGHT.textMuted,faint:LIGHT.textFaint,hover:LIGHT.hover,active:LIGHT.active,accent:scheme.accent}}else{palette={bg:"#111111",sidebar:"#0e0e0e",panel:"#191919",composer:"#141414",surface:"#1c1c1c",surfaceRaised:"#222222",surfaceOverlay:"#2a2a2a",surfaceInput:"#191919",page:"#111111",plate:"#161616",plateStrong:"#222222",border:hairlineBorder(false,0.10),borderSoft:hairlineBorder(false,0.06),borderSubtle:hairlineBorder(false,0.08),borderFocus:mixHex(resolvedInk,"#111111",0.15),text:"#eeeeee",textSoft:"rgba(238,238,238,0.92)",muted:"rgba(238,238,238,0.62)",faint:"rgba(238,238,238,0.45)",hover:"#242424",active:"#2e2e2e",accent:scheme.accent}}var root=document.documentElement;var accent=palette.accent;var hover=accentHover(accent);var rgb=parseHex(accent);root.dataset.appearance=mode;root.style.colorScheme=mode;try{var ysh=window.YatisharaShell;if(ysh&&ysh.setAppearance)ysh.setAppearance(mode)}catch(e){}root.dataset.theme=sid;root.dataset.studioBgFamily=bgFamily;root.dataset.studioBgPack=bgFamily==="animated"?"worlds":bgFamily;if(wpState&&wpState.current&&wpState.current.kind==="asset"){root.dataset.wallpaperKind="asset";root.dataset.wallpaperAssetId=wpState.current.assetId}else{root.dataset.wallpaperKind="preset"}root.style.setProperty("--mos-bg",palette.bg);root.style.setProperty("--mos-sidebar",palette.sidebar);root.style.setProperty("--mos-panel",palette.panel);root.style.setProperty("--mos-raised",palette.surfaceRaised);root.style.setProperty("--mos-page",palette.page||palette.panel);root.style.setProperty("--mos-plate",palette.plate||palette.bg);root.style.setProperty("--mos-plate-strong",palette.plateStrong||palette.surfaceRaised);root.style.setProperty("--mos-composer",palette.composer);root.style.setProperty("--mos-surface",palette.surface);root.style.setProperty("--mos-border",palette.border);root.style.setProperty("--mos-border-soft",palette.borderSoft);root.style.setProperty("--mos-border-subtle",palette.borderSubtle);root.style.setProperty("--mos-text",palette.text);root.style.setProperty("--mos-text-soft",palette.textSoft);root.style.setProperty("--mos-text-bright",palette.text);root.style.setProperty("--color-cursor-text",palette.text);root.style.setProperty("--color-cursor-text-bright",palette.text);root.style.setProperty("--color-cursor-text-soft",palette.textSoft);root.style.setProperty("--mos-muted",palette.muted);root.style.setProperty("--mos-faint",palette.faint);root.style.setProperty("--mos-accent",accent);root.style.setProperty("--mos-accent-hover",hover);root.style.setProperty("--mos-accent-fg",resolvedFg);root.style.setProperty("--mos-secondary",resolvedSecondary);root.style.setProperty("--primary-foreground",resolvedFg);root.style.setProperty("--cursor-accent-fg",resolvedFg);root.style.setProperty("--mos-hover",palette.hover);root.style.setProperty("--mos-active",palette.active);root.style.setProperty("--cursor-accent",accent);root.style.setProperty("--cursor-accent-hover",hover);root.style.setProperty("--cursor-accent-dim",accentDim(accent));root.style.setProperty("--cursor-accent-border",accentBorder(resolvedInk));root.style.setProperty("--cursor-accent-ink",resolvedInk);root.style.setProperty("--mos-accent-ink",resolvedInk);root.style.setProperty("--cursor-sidebar",palette.sidebar);root.style.setProperty("--color-cursor-bg",palette.bg);root.style.setProperty("--color-cursor-border-subtle",palette.borderSubtle);root.style.setProperty("--accent-rgb",rgb.r+","+rgb.g+","+rgb.b);var ov=isLight?{subtle:"rgba(0,0,0,0.04)",hover:"rgba(0,0,0,0.06)",muted:"rgba(0,0,0,0.025)"}:{subtle:"rgba(255,255,255,0.05)",hover:"rgba(255,255,255,0.08)",muted:"rgba(255,255,255,0.03)"};root.style.setProperty("--cursor-overlay-subtle",ov.subtle);root.style.setProperty("--cursor-overlay-hover",ov.hover);root.style.setProperty("--cursor-overlay-muted",ov.muted);root.style.setProperty("--cursor-surface",palette.surface);root.style.setProperty("--cursor-surface-raised",palette.surfaceRaised);root.style.setProperty("--cursor-surface-overlay",palette.surfaceOverlay);root.style.setProperty("--cursor-surface-input",palette.surfaceInput);root.style.setProperty("--cursor-border-subtle",palette.borderSubtle);root.style.setProperty("--cursor-border-focus",palette.borderFocus);root.style.setProperty("--danger","#dc2626");root.style.setProperty("--danger-fg","#ffffff");root.style.setProperty("--danger-rgb","220,38,38");root.style.setProperty("--danger-dim","rgba(220,38,38,0.14)");/* legacy markdown/chat tokens applyTheme() sets; without them .mos-md outside DM bubbles fell back to dark-chat greys on a normal load */root.style.setProperty("--accent",accent);root.style.setProperty("--accent-dim",accentDim(accent));root.style.setProperty("--text",palette.text);root.style.setProperty("--text-muted",palette.muted);root.style.setProperty("--text-faint",palette.faint);root.style.setProperty("--chat-text-color",palette.text);var wpOverride=assetUrlHint;${wallpaperBoot}}catch(e){}})();`;
}
